import { apiGet, apiPost } from '../../../shared/api-client/index.js';

export interface ContactDealSummary {
  id: string;
  title: string;
  stage: string;
  amount: number | string;
  currency: string;
}

export interface ContactInteractionItem {
  id: string;
  type: string;
  subject?: string | null;
  body?: string | null;
  channel?: string | null;
  occurredAt: string;
}

export interface ContactItem {
  id: string;
  workspaceId: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  source?: string | null;
  tags: string[];
  notes?: string | null;
  deals: ContactDealSummary[];
  interactions: ContactInteractionItem[];
  fullName: string;
  dealsCount: number;
}

export interface ListContactsFilters {
  q?: string;
}

export interface CreateContactInput {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

function enhanceContact(contact: Omit<ContactItem, 'fullName' | 'dealsCount'>): ContactItem {
  return {
    ...contact,
    fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' '),
    dealsCount: contact.deals.length,
  };
}

export function getContactsQueryKey(workspaceId: string, filters: ListContactsFilters = {}): readonly [string, string, string] {
  return ['contacts', workspaceId, filters.q?.trim() || 'all'] as const;
}

export function getContactDetailQueryKey(workspaceId: string, contactId: string): readonly [string, string, string] {
  return ['contact', workspaceId, contactId] as const;
}

export async function listContacts(filters: ListContactsFilters = {}): Promise<ContactItem[]> {
  const search = new URLSearchParams();
  if (filters.q?.trim()) {
    search.set('q', filters.q.trim());
  }

  const path = search.size > 0 ? `/contacts?${search.toString()}` : '/contacts';
  const contacts = await apiGet<Array<Omit<ContactItem, 'fullName' | 'dealsCount'>>>(path);
  return contacts.map(enhanceContact);
}

export async function getContactById(contactId: string): Promise<ContactItem> {
  const contact = await apiGet<Omit<ContactItem, 'fullName' | 'dealsCount'>>(`/contacts/${contactId}`);
  return enhanceContact(contact);
}

export async function createContact(input: CreateContactInput): Promise<ContactItem> {
  const contact = await apiPost<Omit<ContactItem, 'fullName' | 'dealsCount'>, CreateContactInput>('/contacts', input);
  return enhanceContact(contact);
}
